import React, { useCallback, useRef, useState } from "react";
import { Users, Upload, Loader2, X, Database, ImageIcon, ImagePlus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AsyncCombobox } from "@/components/AsyncCombobox";
import { UsageCost } from "@/components/UsageCost";
import { IconEditor } from "@/components/IconEditor";
import { ScreenshotTips, LowResWarning, lowResFiles, readingIsUnclear } from "@/components/ScreenshotTips";
import { api, uploadUrl } from "@/lib/api";

function SourceTabs({ source, setSource, disabled }) {
  const tabs = [["database", "Pick an existing card", Database], ["screenshot", "Upload a screenshot", ImageIcon]];
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="card-source-tabs">
      {tabs.map(([id, label, Icon]) => (
        <button
          key={id}
          type="button"
          disabled={disabled}
          onClick={() => setSource(id)}
          className={`px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${source === id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}
          data-testid={`card-source-${id}`}
        >
          <Icon className="w-3.5 h-3.5" /> {label}
        </button>
      ))}
    </div>
  );
}

function ScreenshotUpload({ onRead, disabled, existing = [], label = "Upload card screenshot(s)", testId = "card-screenshot" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const read = async (files) => {
    setPending(null);
    setBusy(true);
    setError(null);
    try {
      onRead(await api.extractCard(files, existing));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!files.length) return;
    const small = await lowResFiles(files);
    if (small.length) setPending({ files, small });
    else read(files);
  };
  return (
    <div>
      <input ref={inputRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" size={existing.length ? "sm" : "default"} disabled={disabled || busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : existing.length ? <ImagePlus className="w-4 h-4 mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
        {busy ? "Reading the card…" : label}
      </Button>
      {!existing.length && (
        <p className="text-[11px] text-muted-foreground mt-1">
          Long card? Select several screenshots at once (up to 8) — they're read together as one card. PNG, JPEG or WEBP, 10 MB each. Images are kept with the saved check.
        </p>
      )}
      {pending && <LowResWarning small={pending.small} onContinue={() => read(pending.files)} onCancel={() => setPending(null)} />}
      {error && <p className="text-xs text-rose-300 mt-1" data-testid={`${testId}-error`}>{error}</p>}
      {error && <div className="mt-2"><ScreenshotTips reason="The card couldn't be read." /></div>}
    </div>
  );
}

const fromExtract = (r) => ({
  name: r.card_name, text: r.card_text, rules: (r.rules || []).map((x) => ({ ...x, is_new: false })),
  image_paths: r.image_paths, image_path: r.image_paths?.[0], source: "screenshot", usage: r.usage,
  icons: (r.icons || []).map((g) => ({ ...g, items: g.items.map((it) => ({ ...it, read_as: it.name })) })), icon_options: r.icon_options,
});

function CardRulesChecklist({ card, onChange, disabled }) {
  const rules = card.rules || [];
  if (!rules.length) return null;
  const patch = (i, p) => onChange({ ...card, rules: rules.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const marked = rules.filter((r) => r.is_new).length;
  return (
    <div className="rounded border border-border bg-background/40 p-3 space-y-2" data-testid="card-rules-checklist">
      <p className="text-xs text-muted-foreground">
        <strong className="text-foreground">Tick the rules that are new or changed</strong> — they are QA&rsquo;d as proposed rules, checked against the rest of the card.
        {marked > 0 && <span className="text-primary ml-1" data-testid="card-rules-marked-count">{marked} marked</span>}
      </p>
      {rules.map((r, i) => (
        <div key={`${r.kind}-${r.name}-${i}`} className={`rounded p-2 ${r.is_new ? "bg-primary/10 border border-primary/40" : ""}`} data-testid={`card-rule-${i}`}>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={Boolean(r.is_new)} onCheckedChange={(v) => patch(i, { is_new: Boolean(v) })} disabled={disabled} data-testid={`card-rule-${i}-new`} />
            <span className="font-medium">{r.name}</span>
            <Badge variant="outline" className="text-[10px]">{r.kind?.replace("_", " ")}</Badge>
            {r.weapon && <span className="text-[11px] text-muted-foreground">on {r.weapon}</span>}
            {r.stats && <span className="text-[11px] font-mono text-muted-foreground">{r.stats}</span>}
          </label>
          {r.is_new && (
            <Textarea
              value={r.text || ""}
              onChange={(e) => patch(i, { text: e.target.value })}
              placeholder="The screenshot only showed the name — paste the full rule text…"
              className="mt-2 min-h-[60px] text-sm"
              disabled={disabled}
              data-testid={`card-rule-${i}-text`}
            />
          )}
          {r.is_new && !(r.text || "").trim() && <p className="text-[11px] text-amber-300 mt-1">Needs rule text before it can be checked.</p>}
        </div>
      ))}
    </div>
  );
}

/** Model card for a check: an existing card from the data, or a screenshot read by the LLM. Editable. */
export function CardSourcePicker({ card, onChange, disabled, optional = false, markNew = true }) {
  const [source, setSource] = useState("database");
  const [loadingCard, setLoadingCard] = useState(false);
  const fetchCards = useCallback(async (q) => {
    const r = await api.listCards({ q, limit: 30 });
    return r.items.map((c) => ({ id: c.id, name: c.name, sub: [c.faction, c.type].filter(Boolean).join(" · ") }));
  }, []);
  const pickCard = async (it) => {
    setLoadingCard(true);
    try {
      const ctx = await api.cardContext(it.id);
      onChange({ name: ctx.name, text: ctx.text, card_id: ctx.card_id, rule_names: ctx.rule_names, rules: (ctx.rules || []).map((x) => ({ ...x, is_new: false })), source: "database" });
    } finally {
      setLoadingCard(false);
    }
  };

  if (!card) {
    return (
      <div className="space-y-2" data-testid="card-source-picker">
        <div className="flex items-center gap-2 flex-wrap">
          <Users className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium">Model card{optional ? " (optional)" : ""}</span>
          <SourceTabs source={source} setSource={setSource} disabled={disabled} />
          {loadingCard && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
        {source === "database" ? (
          <AsyncCombobox placeholder="Search model cards…" triggerLabel="Pick the model card…" icon={<Users className="w-4 h-4 text-cyan-400" />} fetcher={fetchCards} onSelect={pickCard} testId="card-picker-combobox" />
        ) : (
          <ScreenshotUpload disabled={disabled} onRead={(r) => onChange(fromExtract(r))} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-cyan-800/50 bg-cyan-950/10 p-3" data-testid="card-source-selected">
      <div className="flex items-center gap-2 flex-wrap">
        <Users className="w-4 h-4 text-cyan-400" />
        <Input value={card.name || ""} onChange={(e) => onChange({ ...card, name: e.target.value })} placeholder="Model name" disabled={disabled} className="h-8 max-w-sm font-semibold" data-testid="card-name-input" />
        <span className="text-[11px] text-muted-foreground">{card.source === "screenshot" ? "read from screenshot — check it" : "from the game data"}</span>
        <Button type="button" size="sm" variant="ghost" className="ml-auto" disabled={disabled} onClick={() => onChange(null)} data-testid="card-clear-btn">
          <X className="w-3.5 h-3.5 mr-1" /> Change card
        </Button>
      </div>
      {card.image_paths?.length > 0 && (
        <div className="flex flex-wrap items-end gap-2" data-testid="card-screenshot-previews">
          {card.image_paths.map((p, i) => (
            <a key={p} href={uploadUrl(p)} target="_blank" rel="noreferrer">
              <img src={uploadUrl(p)} alt={`Card screenshot ${i + 1}`} className="h-28 rounded border border-border" data-testid={`card-screenshot-preview-${i}`} />
            </a>
          ))}
          <ScreenshotUpload
            disabled={disabled}
            existing={card.image_paths}
            label="Add more screenshots"
            testId="card-screenshot-more"
            onRead={(r) => onChange(fromExtract(r))}
          />
        </div>
      )}
      {card.source === "screenshot" && readingIsUnclear(card) && <ScreenshotTips />}
      <div>
        <Textarea value={card.text || ""} onChange={(e) => onChange({ ...card, text: e.target.value })} disabled={disabled} className="min-h-[160px] font-mono text-xs" data-testid="card-text-input" />
      </div>
      {card.icons?.length > 0 && <IconEditor card={card} onChange={onChange} disabled={disabled} />}
      {markNew && <CardRulesChecklist card={card} onChange={onChange} disabled={disabled} />}
      <UsageCost usage={card.usage} label="Card reading" testId="card-read-usage" />
    </div>
  );
}
